import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import api from '../../services/api';
import { useColor, useTheme, useBrand } from '../../context/ColorContext';
import { ChevronLeft, ChevronRight, Camera, Upload, Loader2, Wand2 } from 'lucide-react';
import BeforeAfterSlider from '../ui/BeforeAfterSlider';
import { toast } from 'sonner';

const isLightColor = (hex) => {
    if (!hex) return true;
    const c = hex.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 150;
};

const HomeVisualizer = ({ onOpenVisualizer, initialIndex = 0, aiPrompt: kartelaPrompt }) => {
    const { activeColor, isFanDeckOpen } = useColor();
    const { theme } = useTheme();
    const { activeBrand } = useBrand();
    const [images, setImages] = useState([]);
    const [activeIndex, setActiveIndex] = useState(initialIndex || 0);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (typeof initialIndex === 'number' && images.length > 0) {
            setActiveIndex(initialIndex % images.length);
        }
    }, [initialIndex, images.length]);

    const [userImage, setUserImage] = useState(null); // RAW Image chosen by user
    const [processedImage, setProcessedImage] = useState(null); // AI Edited Image
    const [isProcessingAI, setIsProcessingAI] = useState(false);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    // Dokunmatik cihazlarda (telefon/tablet) kamera ile çekim seçeneğini de göster
    const isTouchDevice = typeof navigator !== 'undefined' && (navigator.maxTouchPoints > 0 || 'ontouchstart' in window);

    useEffect(() => {
        const fetchImages = async () => {
            try {
                const res = await axios.get('/api/home-visualizer');
                setImages(res.data);
            } catch (error) {
                console.error('Home visualizer images fetch error:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchImages();
    }, []);

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Okuma ve ekranda gösterme (Base64'e çevir)
        const reader = new FileReader();
        reader.onload = async (ev) => {
            const base64Image = ev.target.result;
            setUserImage(base64Image);
            setProcessedImage(null); // Yeni resim yüklendiğinde eski AI sonucunu sıfırla
            // AI işleme işlemi artık "RENK UYGULA" butonuna tıklandığında manuel yapılacak.
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const processImageWithAI = async (base64Img, targetColor) => {
        if (!base64Img) return;
        setIsProcessingAI(true);
        toast.info('Yapay zeka odanızı boyuyor. Lütfen bekleyin...', { duration: 5000 });

        try {
            const defaultPrompt = `Paint the walls of this room with color hex ${targetColor.code}. Keep the furniture, lighting, and textures realistic and intact. Only change the wall paint color.`;
            const prompt = kartelaPrompt
                ? kartelaPrompt.replace(/\{color\}/g, targetColor.code)
                : defaultPrompt;

            // Backend endpointimize gönderiyoruz
            const res = await api.post('/fal-ai/edit', {
                prompt: prompt,
                image_url: base64Img
            });

            if (res.data && res.data.image && res.data.image.url) {
                setProcessedImage(res.data.image.url);
                toast.success('Odanız başarıyla boyandı!');
            } else if (res.data && res.data.image_url) {
                setProcessedImage(res.data.image_url);
                toast.success('Odanız başarıyla boyandı!');
            } else if (res.data && res.data.images && res.data.images[0]?.url) {
                setProcessedImage(res.data.images[0].url);
                toast.success('Odanız başarıyla boyandı!');
            } else {
                console.error("Beklenmeyen API Yanıtı:", res.data);
                throw new Error("Geçersiz API yanıtı.");
            }
        } catch (error) {
            console.error("AI İşleme Hatası:", error);
            const errMsg = error.response?.data?.error || "Oda boyanırken bir hata oluştu. Ayarlardan API anahtarını kontrol edin.";
            toast.error(errMsg, { duration: 6000 });
        } finally {
            setIsProcessingAI(false);
        }
    };

    // Renk değiştiğinde elimizde "kendi yüklediğimiz" resim varsa yeniden AI'dan boyamasını iste (İPTAL EDİLDİ: Kullanıcı butona basarak kendi tetikleyecek)
    // useEffect(() => {
    //     // Eğer kullanıcı fotoğraf yüklemişse ve işlenmiyorsa (yeni renk seçildi tetiği)
    //     if (userImage && !isProcessingAI && !isLoading) {
    //         processImageWithAI(userImage, activeColor);
    //     }
    // }, [activeColor.code]); // Sadece renk kodu değiştiğinde tetiklenir

    const handleManualAITrigger = () => {
        if (userImage && !isProcessingAI && !isLoading) {
            processImageWithAI(userImage, activeColor);
        } else if (!userImage) {
            toast.error("Lütfen önce kendi odanızı yükleyin.");
        }
    };

    const nextImage = () => setActiveIndex((prev) => (prev + 1) % images.length);
    const prevImage = () => setActiveIndex((prev) => (prev - 1 + images.length) % images.length);

    const isDark = theme === 'dark';
    const brandPrimaryColor = activeBrand.primaryColor;
    const light = isLightColor(activeColor.code);

    // Default placeholder
    const placeholderImage = 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80';
    const hasImages = images.length > 0;
    const roomImage = hasImages ? images[activeIndex]?.url : placeholderImage;
    const displayImage = processedImage || userImage || roomImage;

    return (
        <div className={`relative z-10 w-full overflow-hidden rounded-[2rem] shadow-2xl flex flex-col lg:flex-row ${isDark ? 'bg-slate-900 border border-white/5' : 'bg-white border border-black/5'} transition-all duration-700 animate-in fade-in slide-in-from-bottom-10 h-full scrollbar-hide`}>
            {/* Left / Top - Visual Area */}
            <div className="relative w-full flex-1 min-h-[40%] lg:h-auto lg:min-h-[450px] shrink-0 overflow-hidden transition-colors duration-500 group" style={{ backgroundColor: activeColor.code || brandPrimaryColor }}>
                {/* Noise texture */}
                <div className="absolute inset-0 opacity-[0.04] bg-[url('/noise.svg')] mix-blend-overlay pointer-events-none z-10" />

                {isProcessingAI ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center z-[25] gap-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
                        <Loader2 size={64} className="animate-spin text-white" style={{ color: brandPrimaryColor }} />
                        <div className="flex flex-col items-center">
                            <h4 className="text-white font-black uppercase tracking-widest text-sm mb-1">DÜFA AI ÇALIŞIYOR</h4>
                            <p className="text-white/70 text-[10px] font-bold tracking-wider">{activeColor.name} rengi odanıza uygulanıyor...</p>
                        </div>
                    </div>
                ) : isLoading && !userImage ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center z-[5] gap-3">
                        <Loader2 size={48} className={`animate-spin ${light ? 'text-black/20' : 'text-white/20'}`} />
                    </div>
                ) : displayImage ? (
                    <>
                        {userImage && processedImage ? (
                            <div className="absolute inset-0 z-[5]">
                                <BeforeAfterSlider beforeImage={userImage} afterImage={processedImage} />
                            </div>
                        ) : (
                            <img
                                src={displayImage}
                                alt="Oda Görseli"
                                className="absolute inset-0 w-full h-full object-cover z-[5] select-none"
                            />
                        )}

                        {/* Navigation Arrows */}
                        {hasImages && images.length > 1 && !userImage && (
                            <>
                                <button
                                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-black/20 hover:bg-black/40 text-white rounded-full backdrop-blur-md transition-all opacity-0 group-hover:opacity-100"
                                >
                                    <ChevronLeft size={24} />
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-black/20 hover:bg-black/40 text-white rounded-full backdrop-blur-md transition-all opacity-0 group-hover:opacity-100"
                                >
                                    <ChevronRight size={24} />
                                </button>
                            </>
                        )}
                    </>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center z-[5] gap-3">
                        <Camera size={48} className={`${light ? 'text-black/20' : 'text-white/20'}`} />
                    </div>
                )}

                {/* Color name overlay */}
                <div className="absolute bottom-0 inset-x-0 z-20 p-4 bg-gradient-to-t from-black/40 to-transparent">
                    <p className="text-white/80 text-[10px] font-black uppercase tracking-widest drop-shadow-lg">
                        {activeColor.name}
                    </p>
                </div>
            </div>

            {/* Right / Bottom - Control Panel */}
            <div className={`w-full lg:w-[340px] shrink lg:shrink-0 flex flex-col p-3 lg:p-8 gap-3 sm:gap-4 lg:gap-6 overflow-y-auto lg:overflow-visible ${isDark ? 'border-t lg:border-t-0 lg:border-l border-white/5' : 'border-t lg:border-t-0 lg:border-l border-black/5'}`}>
                {/* Header */}
                <div>
                    <span className="text-[9px] font-black uppercase tracking-[0.3em] opacity-40">Odanı Canlandır</span>
                    <h3 className={`text-lg font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {activeBrand?.name || 'DÜFA AI'} İLHAMI
                    </h3>
                </div>

                {/* Selected Color Info */}
                <div className={`p-4 rounded-2xl flex items-center gap-4 ${isDark ? 'bg-white/5 border border-white/5' : 'bg-slate-50 border border-black/5'}`}>
                    <div
                        className="w-14 h-14 rounded-xl shadow-lg border-4 shrink-0 transition-colors duration-500"
                        style={{
                            backgroundColor: activeColor.code || brandPrimaryColor,
                            borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.9)'
                        }}
                    />
                    <div className="min-w-0 flex-1">
                        <span className={`text-[9px] font-black uppercase tracking-widest ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Seçili Renk</span>
                        <p className={`text-sm font-black uppercase tracking-tight truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {activeColor.name || 'Renk Seçin'}
                        </p>
                        <p className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                            {activeColor.code || '-'}
                        </p>
                    </div>
                </div>

                {userImage ? (
                    <div className="flex flex-col gap-2">
                        <button
                            onClick={handleManualAITrigger}
                            disabled={isProcessingAI}
                            className={`w-full py-4 rounded-xl font-black uppercase tracking-[0.15em] text-white text-[11px] shadow-xl hover:brightness-110 active:scale-[0.97] transition-all flex items-center justify-center gap-2.5 ${isProcessingAI ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                            style={{ backgroundColor: brandPrimaryColor }}
                        >
                            {isProcessingAI ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
                            {isProcessingAI ? 'İŞLENİYOR...' : 'RENK UYGULA'}
                        </button>
                        <button
                            onClick={() => {
                                setUserImage(null);
                                setProcessedImage(null);
                            }}
                            disabled={isProcessingAI}
                            className={`w-full py-3 rounded-xl font-bold uppercase tracking-widest text-[10px] border active:scale-[0.97] transition-all flex items-center justify-center gap-2 ${isDark ? 'border-white/20 text-white/70 hover:bg-white/5' : 'border-black/20 text-slate-600 hover:bg-slate-50'}`}
                        >
                            Başka Oda Yükle
                        </button>
                    </div>
                ) : (
                    <div className="w-full flex gap-2">
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isProcessingAI}
                            className={`flex-1 py-4 rounded-xl font-black uppercase tracking-[0.12em] text-white text-[11px] shadow-xl hover:brightness-110 active:scale-[0.97] transition-all flex items-center justify-center gap-2 ${isProcessingAI ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                            style={{ backgroundColor: brandPrimaryColor }}
                        >
                            <Upload size={16} />
                            {isTouchDevice ? 'Galeri' : 'Kendi Odanı Yükle'}
                        </button>
                        {isTouchDevice && (
                            <button
                                onClick={() => cameraInputRef.current?.click()}
                                disabled={isProcessingAI}
                                className={`flex-1 py-4 rounded-xl font-black uppercase tracking-[0.12em] text-white text-[11px] shadow-xl hover:brightness-110 active:scale-[0.97] transition-all flex items-center justify-center gap-2 ${isProcessingAI ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                                style={{ backgroundColor: brandPrimaryColor }}
                            >
                                <Camera size={16} />
                                Fotoğraf Çek
                            </button>
                        )}
                    </div>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileChange}
                />
                {/* Kamera ile çekim (telefon/tablet): arka kamerayı açar */}
                <input
                    ref={cameraInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileChange}
                />

                {/* Image Dots */}
                {hasImages && images.length > 1 && !userImage && (
                    <div className="flex justify-center gap-2 mt-2">
                        {images.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => setActiveIndex(idx)}
                                className={`w-2 h-2 rounded-full transition-all ${idx === activeIndex ? 'w-6' : 'bg-slate-300 dark:bg-white/10'}`}
                                style={{ backgroundColor: idx === activeIndex ? brandPrimaryColor : undefined }}
                            />
                        ))}
                    </div>
                )}

                <p className={`text-[10px] font-medium leading-relaxed text-center mt-auto ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                    Kartelayı kaydırarak seçtiğiniz rengi oda üzerinde görün. Veya kendi odanızı yükleyin.
                </p>
            </div>
        </div>
    );
};

export default HomeVisualizer;
